import httpClient from './httpClient';

const planillaCerradaService = {
    /**
     * Obtener empleados cerrados de la planilla del periodo (la fecha da el ID de la planilla).
     * GET /api/planillas-cerradas/por-periodo?empresa_id=&anio=&mes=
     * Backend busca la planilla de ese mes/año y devuelve la misma estructura que obtenerEmpleadosCerrados.
     */
    obtenerEmpleadosCerradosPorPeriodo: async (empresaId, anio, mes) => {
        try {
            const response = await httpClient.get('/planillas-cerradas/por-periodo', {
                params: { empresa_id: empresaId, anio, mes },
            });
            return response.data;
        } catch (error) {
            console.error('❌ Error obteniendo empleados cerrados por periodo:', error);
            throw error;
        }
    },

    /**
     * Verificar si una planilla está cerrada
     */
    verificarEstadoCerrada: async (planillaId) => {
        try {
            console.log('🔄 Verificando estado de planilla cerrada:', planillaId);
            const response = await httpClient.get(`/planillas-cerradas/${planillaId}/verificar-estado`);
            console.log('✅ Estado verificado:', response.data);
            return response.data;
        } catch (error) {
            console.error('❌ Error verificando estado de planilla cerrada:', error);
            throw error;
        }
    },

    /**
     * Obtener empleados cerrados con sus totales calculados
     */
    obtenerEmpleadosCerrados: async (planillaId) => {
        try {
            console.log('🔄 Obteniendo empleados cerrados:', planillaId);
            const response = await httpClient.get(`/planillas-cerradas/${planillaId}/empleados`);
            console.log('✅ Empleados cerrados obtenidos:', response.data?.data?.length || 0);
            return response.data;
        } catch (error) {
            console.error('❌ Error obteniendo empleados cerrados:', error);
            throw error;
        }
    },

    /**
     * Obtener conceptos cerrados agrupados por empleado
     */
    obtenerConceptosCerrados: async (planillaId) => {
        try {
            console.log('🔄 Obteniendo conceptos cerrados:', planillaId);
            const response = await httpClient.get(`/planillas-cerradas/${planillaId}/conceptos`);
            console.log('✅ Conceptos cerrados obtenidos:', Object.keys(response.data?.data || {}).length);
            return response.data;
        } catch (error) {
            console.error('❌ Error obteniendo conceptos cerrados:', error);
            throw error;
        }
    },

    /**
     * Obtener resumen completo de planilla cerrada
     */
    obtenerResumenCompleto: async (planillaId) => {
        try {
            console.log('🔄 Obteniendo resumen completo:', planillaId);
            const response = await httpClient.get(`/planillas-cerradas/${planillaId}/resumen`);
            console.log('✅ Resumen completo obtenido:', response.data);
            return response.data;
        } catch (error) {
            console.error('❌ Error obteniendo resumen completo:', error);
            throw error;
        }
    },

    /**
     * Transformar datos de empleados cerrados para compatibilidad con frontend
     */
    transformarEmpleadosCerrados: (empleadosCerrados) => {
        return empleadosCerrados.map(emp => {
            return {
                // Datos básicos del empleado
                id: emp.id || emp.empleado_id,
                empleado_cerrado_id: emp.empleado_cerrado_id,
                nombre_completo: emp.nombre_completo,
                numero_documento: emp.numero_documento,
                cargo: emp.cargo,
                categoria: emp.categoria,
                areas_de_trabajo: emp.areas_de_trabajo,
                proyecto: emp.proyecto,
                sueldo_basico: emp.sueldo_basico != null ? parseFloat(emp.sueldo_basico) : null,
                
                // ✅ CAMPOS DE CONTACTO Y EXTRAS
                telefono: emp.telefono,
                email: emp.email,
                direccion: emp.direccion,
                descuento_descanso: emp.descuento_descanso,
                
                // 💳 INFORMACIÓN BANCARIA
                banco: emp.banco || null,
                cuenta_bancaria: emp.cuenta_bancaria || null,
                cci: emp.cci || null,
                
                // Datos laborales
                fecha_ingreso: emp.fecha_ingreso_actual,
                fecha_cese: emp.fecha_cese_actual,
                dias_laborados: emp.dias_laborados_periodo,
                antiguedad_meses: emp.antiguedad_meses,
                
                // Régimen laboral
                regimen_laboral: {
                    id: emp.regimen_laboral_id,
                    codigo: emp.regimen_laboral_codigo,
                    nombre: emp.regimen_laboral_nombre
                },
                
                // Sistema pensionario
                sistema_pensionario: emp.sistema_pensionario,
                afp: emp.afp_nombre ? {
                    id: emp.afp_id,
                    nombre: emp.afp_nombre,
                    cuspp: emp.cuspp,
                    tipo_afp: emp.tipo_afp
                } : null,
                
                // Totales calculados
                totales: {
                    totalIngresos: parseFloat(emp.total_ingresos || 0),
                    totalDescuentos: parseFloat(emp.total_descuentos || 0),
                    totalAportes: parseFloat(emp.total_aportes || 0),
                    netoAPagar: parseFloat(emp.neto_a_pagar || 0)
                },
                
                // Mantener boleta_calculada si viene del backend
                boleta_calculada: emp.boleta_calculada || null,
                
                // Metadata
                fecha_cierre: emp.fecha_cierre,
                es_planilla_cerrada: true
            };
        });
    },

    /**
     * Integrar conceptos cerrados con empleados
     */
    integrarConceptosConEmpleados: (empleados, conceptosPorEmpleado) => {
        return empleados.map(empleado => {
            
            // 🎯 Si el empleado ya tiene boleta_calculada del backend, usarla directamente
            if (empleado.boleta_calculada && empleado.boleta_calculada.data) {
                console.log('📋 Empleado ya tiene boleta_calculada completa:', empleado.nombre_completo);
                console.log('  📊 Ingresos:', empleado.boleta_calculada.data.dataIngresos?.length || 0);
                console.log('  📊 Descuentos:', empleado.boleta_calculada.data.dataDescuentos?.length || 0);
                console.log('  📊 Aportes:', empleado.boleta_calculada.data.dataAportes?.length || 0);
                
                return {
                    ...empleado,
                    // Mantener la boleta_calculada original del backend cerrado
                    boleta_calculada: empleado.boleta_calculada
                };
            }

            // 🔧 Si no tiene boleta_calculada, construirla desde conceptos agrupados (fallback)
            console.log('🔧 Construyendo boleta_calculada desde conceptos para:', empleado.nombre_completo);
            const conceptosEmpleado = conceptosPorEmpleado[empleado.id] || {
                ingresos: [],
                descuentos: [],
                aportes: []
            };

            return {
                ...empleado,
                boleta_calculada: {
                    data: {
                        totales: empleado.totales || {
                            totalIngresos: empleado.total_ingresos || 0,
                            totalDescuentos: empleado.total_descuentos || 0,
                            totalAportes: empleado.total_aportes || 0,
                            netoAPagar: empleado.neto_a_pagar || 0
                        },
                        dataIngresos: conceptosEmpleado.ingresos,
                        dataDescuentos: conceptosEmpleado.descuentos,
                        dataAportes: conceptosEmpleado.aportes
                    }
                }
            };
        });
    },

    /**
     * Obtener todos los datos necesarios para planilla cerrada
     */
    obtenerDatosCompletos: async (planillaId) => {
        try {
            console.log('🔒 Cargando datos de planilla cerrada:', planillaId);
            
            // 🚀 OPTIMIZACIÓN: Solo cargar empleados (que ya incluyen conceptos en boleta_calculada)
            console.log('🚀 Cargando empleados cerrados con conceptos integrados...');
            const empleadosResponse = await planillaCerradaService.obtenerEmpleadosCerrados(planillaId);

            if (!empleadosResponse.success) {
                throw new Error('Error al obtener empleados de planilla cerrada');
            }

            // Verificar si hay datos cerrados
            const tieneEmpleados = empleadosResponse.data && empleadosResponse.data.length > 0;

            if (!tieneEmpleados) {
                console.warn('⚠️ Planilla sin datos cerrados:', empleadosResponse.mensaje || 'Sin empleados cerrados');
                // Devolver estructura vacía para compatibilidad
                return {
                    empleados: [],
                    conceptos: {},
                    resumen: {
                        total_empleados: 0,
                        total_conceptos: 0
                    },
                    compensaciones_planilla: empleadosResponse.compensaciones_planilla || [],
                    mensaje: empleadosResponse.mensaje || 'Planilla sin datos cerrados'
                };
            }

            // Verificar si los empleados ya tienen boleta_calculada completa
            const primerEmpleado = empleadosResponse.data[0];
            const tieneBoletaCalculada = primerEmpleado && primerEmpleado.boleta_calculada && primerEmpleado.boleta_calculada.data;
            
            console.log('🎯 Empleados tienen boleta_calculada:', tieneBoletaCalculada);
            console.log('🎯 Ejemplo de estructura:', {
                empleado: primerEmpleado?.nombre_completo,
                tiene_boleta: !!primerEmpleado?.boleta_calculada,
                ingresos: primerEmpleado?.boleta_calculada?.data?.dataIngresos?.length || 0,
                descuentos: primerEmpleado?.boleta_calculada?.data?.dataDescuentos?.length || 0,
                aportes: primerEmpleado?.boleta_calculada?.data?.dataAportes?.length || 0
            });

            // Transformar empleados (el frontend service ya maneja boleta_calculada existente)
            const empleadosTransformados = planillaCerradaService.transformarEmpleadosCerrados(
                empleadosResponse.data
            );

            // Ya no necesitamos integración separada - los datos vienen completos del backend
            const empleadosConConceptos = planillaCerradaService.integrarConceptosConEmpleados(
                empleadosTransformados,
                {} // Conceptos vacíos porque ya están en boleta_calculada
            );

            // Contar conceptos totales desde las boletas_calculadas
            let totalConceptos = 0;
            empleadosConConceptos.forEach(emp => {
                if (emp.boleta_calculada?.data) {
                    totalConceptos += (emp.boleta_calculada.data.dataIngresos?.length || 0);
                    totalConceptos += (emp.boleta_calculada.data.dataDescuentos?.length || 0);
                    totalConceptos += (emp.boleta_calculada.data.dataAportes?.length || 0);
                }
            });

            console.log('✅ Datos de planilla cerrada cargados OPTIMIZADO:', {
                total_empleados: empleadosConConceptos.length,
                total_conceptos: totalConceptos,
                optimizacion: 'Conceptos incluidos en empleados'
            });

            return {
                empleados: empleadosConConceptos,
                conceptos: {}, // Ya no necesarios - están en boleta_calculada
                resumen: {
                    total_empleados: empleadosResponse.total_empleados || empleadosConConceptos.length,
                    total_conceptos: totalConceptos
                },
                compensaciones_planilla: empleadosResponse.compensaciones_planilla || [],
                mensaje: null // Todo cargado correctamente
            };

        } catch (error) {
            console.error('❌ Error obteniendo datos completos de planilla cerrada:', error);
            throw error;
        }
    }
};

export default planillaCerradaService;
 
 
 
 
 
 